import { Button, Card, Space, Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useContext, useEffect, useState } from "react";
import {
  CustomerProps,
  TagProps,
  TaskProps,
} from "../../interfaces/interfaces";
import {
  EditOutlined,
  CopyOutlined,
  MailOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  IssuesCloseOutlined,
  PhoneOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataContext from "../../context/DataContext";
import Link from "antd/es/typography/Link";

export const TaskListTemplate = ({
  taskList,
  onAddNewTask,
  onEditTask,
  onDuplicateTask,
  onDeleteTask,
  onSelectCustomer,
}: {
  taskList: TaskProps[];
  onAddNewTask: () => void;
  onEditTask: (record: TaskProps, index?: number) => void;
  onDuplicateTask: (record: TaskProps) => void;
  onDeleteTask?: (record: TaskProps, index?: number) => void;
  onSelectCustomer?: (customer: string) => void;
}) => {
  const { dataLoading, getData } = useContext(DataContext);

  const taskColumns: ColumnsType<TaskProps> = [
    {
      title: "Actions",
      key: "edit",
      render: (params, _, index) => (
        <Space direction="horizontal">
          <Tooltip title="Edit" placement="top">
            <Button
              style={{ color: "gray", borderColor: "gray" }}
              onClick={() => onEditTask(params, index)}
              icon={<EditOutlined />}
              size="small"
            ></Button>
          </Tooltip>
          <Tooltip title="Duplicate" placement="top">
            <Button
              style={{ color: "gray", borderColor: "gray" }}
              onClick={() => onDuplicateTask(params)}
              icon={<CopyOutlined />}
              size="small"
            ></Button>
          </Tooltip>
          {onDeleteTask && (
            <Tooltip title="Delete" placement="top">
              <Button
                style={{ color: "gray", borderColor: "gray" }}
                onClick={() => onDeleteTask(params, index)}
                icon={<DeleteOutlined />}
                size="small"
              ></Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "typeOfTask",
      key: "typeOfTask",
      render: (typeOfTask: TaskProps["typeOfTask"]) =>
        typeOfTask === "Call" ? (
          <>
            <Tag color="lime" icon={<PhoneOutlined />}>
              Call
            </Tag>
          </>
        ) : typeOfTask === "Email" ? (
          <>
            <Tag color="cyan" icon={<MailOutlined />}>
              Email
            </Tag>
          </>
        ) : typeOfTask === "Follow-up" ? (
          <>
            <Tag color="magenta" icon={<CheckCircleOutlined />}>
              Follow-up
            </Tag>
          </>
        ) : typeOfTask === "Meeting" ? (
          <>
            <Tag color="geekblue" icon={<TeamOutlined />}>
              Meeting
            </Tag>
          </>
        ) : (
          <Tag color="orange" icon={<IssuesCloseOutlined />}>
            {typeOfTask}
          </Tag>
        ),
      sorter: (a, b) => a.typeOfTask.localeCompare(b.typeOfTask),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "subject",
      width: 200,
      render: (customer: CustomerProps) => (
        <>
          {customer && customer._id && (
            <div style={{ minWidth: "120px" }}>
              {onSelectCustomer ? (
                <Link>{customer?.DisplayName || ""}</Link>
              ) : (
                <>{customer?.DisplayName || ""}</>
              )}
            </div>
          )}
        </>
      ),
      onCell: (record) => {
        return {
          onClick: () =>
            record.customer?._id && onSelectCustomer?.(record.customer?._id),
        };
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (description) => (
        <div
          style={{
            maxWidth: "140px",
            maxHeight: "130px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {description}
        </div>
      ),
      responsive: ["xl", "lg"],
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (priority: string) => (
        <Tag
          color={
            priority === "High"
              ? "red"
              : priority === "Medium"
                ? "orange"
                : "green"
          }
        >
          {priority}
        </Tag>
      ),
      sorter: (a, b) => a.priority.localeCompare(b.priority),

      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Date",
      dataIndex: "startDate",
      className: "align-center",
      key: "startDate",
      render: (startDate: string) =>
        startDate ? dayjs(startDate).format("MM/DD/YYYY") : "-",
      sorter: (a, b) =>
        (a.startDate
          ? dayjs(a.startDate).format("YYYY-MM-DD")
          : ""
        ).localeCompare(
          b.startDate ? dayjs(b.startDate).format("YYYY-MM-DD") : "",
        ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Next",
      dataIndex: "nextDate",
      className: "align-center",
      key: "nextDate",
      render: (nextDate: string) =>
        nextDate ? dayjs(nextDate).format("MM/DD/YYYY") : "-",
      sorter: (a, b) =>
        (a.nextDate
          ? dayjs(a.nextDate).format("YYYY-MM-DD")
          : ""
        ).localeCompare(
          b.nextDate ? dayjs(b.nextDate).format("YYYY-MM-DD") : "",
        ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag
          color={
            status === "Pending"
              ? "red"
              : status === "In Progress"
                ? "gold"
                : status === "Cancelled"
                  ? "blue"
                  : "green"
          }
        >
          {status}
        </Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: "20%",
      render: (tags: TagProps[]) => (
        <div>
          {tags?.map((tag: TagProps) => (
            <Tag
              key={tag._id}
              color={tag.color}
              bordered={false}
              style={{
                maxWidth: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tag.name}
            </Tag>
          ))}
        </div>
      ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Recurring",
      dataIndex: "isRecurring",
      key: "isRecurring",
      render: (isRecurring: boolean, record: TaskProps) =>
        isRecurring ? <Tag color="purple">{record.frequency}</Tag> : "-",
      sorter: (a, b) =>
        (a.frequency ? a.frequency : "").localeCompare(
          b.frequency ? b.frequency : "",
        ),
      responsive: ["xl"],
    },
    {
      title: "Assigned",
      dataIndex: "assignedTo",
      key: "assignedTo",
      render: (_, row) => (
        <>
          {Array.isArray(row.assignedTo) ? (
            row.assignedTo?.map((assigned) => (
              <div
                key={assigned._id}
                color="blue"
                style={{
                  maxWidth: "120px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {assigned?.name}
              </div>
            ))
          ) : (
            <div
              style={{
                maxWidth: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.assignedTo?.name}
            </div>
          )}
        </>
      ),
    },
  ];
  return (
    <Card
      title={
        <Space>
          <div>Tasks</div>
          <Tooltip title="New">
            <Button icon={<PlusOutlined />} onClick={onAddNewTask}></Button>
          </Tooltip>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Table
        style={{ width: "100%", overflowX: "scroll" }}
        loading={dataLoading}
        columns={taskColumns}
        dataSource={taskList}
        rowKey="_id"
        pagination={{ pageSize: 5 }}
        size="small"
        tableLayout="auto"
        locale={{ emptyText: "No tasks " }}
      />
    </Card>
  );
};
